import React, { useState, useEffect } from 'react';
import { Upload, Globe, Lock } from 'lucide-react';
import axios from 'axios';
import api from '../services/api';

type SourceTab = 'text' | 'pdf' | 'url' | 'youtube' | 'bank';

type BankSelectionMode = 'all' | 'pick' | 'random';

interface BankQuestion {
  _id: string;
  question: string;
  options: string[];
  correctAnswer: string;
}

interface BankOption {
  _id: string;
  name: string;
  questions: BankQuestion[];
}

interface CreateExamModalProps {
  onClose: () => void;
  onCreate: (data: {
    tab: SourceTab;
    topic?: string;
    sourceUrl?: string;
    pdfFile?: File;
    youtubeUrl?: string;
    questionCount: number;
    timerSeconds: number;
    bankId?: string;
    questionIds?: string[];
    randomCount?: number;
    extraAiTopic?: string;
    extraAiCount?: number;
  }) => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';
const TIMER_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const CreateExamModal: React.FC<CreateExamModalProps> = ({ onClose, onCreate }) => {
  const [activeTab, setActiveTab] = useState<SourceTab>('text');
  const [topic, setTopic] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [questionCount, setQuestionCount] = useState(10);
  const [timerSeconds, setTimerSeconds] = useState(10);

  // Bank tab state
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [bankSelectionMode, setBankSelectionMode] = useState<BankSelectionMode>('all');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [bankRandomCount, setBankRandomCount] = useState(10);
  const [extraAiTopic, setExtraAiTopic] = useState('');
  const [extraAiCount, setExtraAiCount] = useState(0);
  const [banksLoaded, setBanksLoaded] = useState(false);

  const subscriptionStatus = localStorage.getItem('subscriptionStatus') || '';
  const isPro = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';

  // Fetch banks when bank tab selected
  useEffect(() => {
    if (activeTab === 'bank' && !banksLoaded) {
      const controller = new AbortController();
      api.get('/banks?limit=100', { signal: controller.signal }).then((res) => {
        setBanks(res.data.banks);
        setBanksLoaded(true);
      }).catch((err) => {
        if (!axios.isCancel(err)) {
          setBanksLoaded(true);
        }
      });
      return () => {
        controller.abort();
      };
    }
  }, [activeTab, banksLoaded]);

  const selectedBank = banks.find((b) => b._id === selectedBankId);

  const toggleQuestionId = (id: string) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTab === 'text' && !topic.trim()) return;
    if (activeTab === 'url' && !sourceUrl.trim()) return;
    if (activeTab === 'pdf' && !pdfFile) return;
    if (activeTab === 'youtube' && !youtubeUrl.trim()) return;
    if (activeTab === 'bank') {
      if (!selectedBankId) return;
      if (bankSelectionMode === 'pick' && selectedQuestionIds.length === 0) return;
    }

    if (activeTab === 'bank') {
      onCreate({
        tab: 'bank',
        questionCount: 0, // not used for bank
        timerSeconds,
        bankId: selectedBankId,
        questionIds: bankSelectionMode === 'pick' ? selectedQuestionIds :
                     bankSelectionMode === 'all' ? selectedBank?.questions.map((q) => q._id) : undefined,
        randomCount: bankSelectionMode === 'random' ? bankRandomCount : undefined,
        extraAiTopic: extraAiCount > 0 ? extraAiTopic : undefined,
        extraAiCount: extraAiCount > 0 ? extraAiCount : undefined,
      });
    } else {
      onCreate({
        tab: activeTab,
        topic: activeTab === 'text' ? topic : undefined,
        sourceUrl: activeTab === 'url' ? sourceUrl : undefined,
        pdfFile: activeTab === 'pdf' ? pdfFile! : undefined,
        youtubeUrl: activeTab === 'youtube' ? youtubeUrl : undefined,
        questionCount,
        timerSeconds,
      });
    }
    onClose();
  };

  const tabs: { key: SourceTab; label: string; proOnly?: boolean }[] = [
    { key: 'text', label: 'Text' },
    { key: 'pdf', label: 'PDF', proOnly: true },
    { key: 'url', label: 'URL', proOnly: true },
    { key: 'youtube', label: 'YouTube', proOnly: true },
    { key: 'bank', label: 'Bank', proOnly: true },
  ];

  const inputClasses = "w-full px-3 py-2 border border-gray-200 rounded-xl mt-1 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[28rem] max-h-[90vh] overflow-y-auto animate-fade-in">
        <h2 className="font-heading text-2xl font-bold mb-4 text-center text-gray-900">Create New Exam</h2>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-full p-1 mb-5">
          {tabs.map(tab => {
            const locked = tab.proOnly && !isPro;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => !locked && setActiveTab(tab.key)}
                className={`flex-1 py-2 text-center text-sm font-medium rounded-full transition flex items-center justify-center gap-1 ${
                  locked
                    ? 'text-gray-400 cursor-not-allowed'
                    : activeTab === tab.key
                      ? 'bg-primary-50 text-primary shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                }`}
                title={locked ? 'Upgrade to Pro to unlock' : undefined}
              >
                {tab.label}
                {locked && <Lock size={12} />}
              </button>
            );
          })}
        </div>

        {/* Pro upsell banner for locked tabs */}
        {tabs.find(t => t.key === activeTab)?.proOnly && !isPro && (
          <div className="mb-4 p-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl border border-primary/10 text-center">
            <p className="text-sm text-gray-700 font-medium">
              Upgrade to <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent font-bold">Pro</span> to create exams from PDF, URL, YouTube, and question banks.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab content */}
          {activeTab === 'text' && (
            <div>
              <label className="block text-gray-700 text-sm font-medium">Topic</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className={inputClasses}
                placeholder="e.g., World War II, JavaScript basics..."
                required
              />
            </div>
          )}

          {activeTab === 'pdf' && (
            <div>
              <label className="block text-gray-700 text-sm font-medium">Upload PDF</label>
              <div className="mt-1 border-2 border-dashed border-primary/30 rounded-xl p-6 text-center hover:border-primary/50 transition">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload" className="cursor-pointer">
                  {pdfFile ? (
                    <p className="text-sm text-gray-700 font-medium">{pdfFile.name}</p>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={24} className="text-primary/50" />
                      <p className="text-sm text-gray-500">Click to select a PDF file (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          )}

          {activeTab === 'url' && (
            <div>
              <label className="block text-gray-700 text-sm font-medium">URL</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className={inputClasses}
                placeholder="https://example.com/article"
                required
              />
            </div>
          )}

          {activeTab === 'youtube' && (
            <div>
              <label className="block text-gray-700 text-sm font-medium">YouTube URL</label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className={inputClasses}
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
              <p className="text-xs text-gray-400 mt-1">The video must have captions (auto-generated or manual).</p>
            </div>
          )}

          {activeTab === 'bank' && (
            <div className="space-y-3">
              <div>
                <label className="block text-gray-700 text-sm font-medium">Question Bank</label>
                <select
                  value={selectedBankId}
                  onChange={(e) => { setSelectedBankId(e.target.value); setSelectedQuestionIds([]); }}
                  className={inputClasses}
                  required
                >
                  <option value="">Select a bank...</option>
                  {banks.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name} ({b.questions.length} questions)
                    </option>
                  ))}
                </select>
              </div>

              {selectedBank && (
                <>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium">Selection mode</label>
                    <div className="flex gap-1 bg-gray-100 rounded-full p-1 mt-1">
                      {([
                        { key: 'all' as BankSelectionMode, label: 'All' },
                        { key: 'pick' as BankSelectionMode, label: 'Pick' },
                        { key: 'random' as BankSelectionMode, label: 'Random' },
                      ]).map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setBankSelectionMode(m.key)}
                          className={`flex-1 py-1.5 text-center text-xs font-medium rounded-full transition ${
                            bankSelectionMode === m.key
                              ? 'bg-primary-50 text-primary shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {bankSelectionMode === 'pick' && (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                      {selectedBank.questions.map((q, i) => (
                        <label key={q._id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded p-1">
                          <input
                            type="checkbox"
                            checked={selectedQuestionIds.includes(q._id)}
                            onChange={() => toggleQuestionId(q._id)}
                            className="mt-0.5"
                          />
                          <span className="text-gray-700 text-xs">
                            <span className="text-gray-400">Q{i + 1}.</span> {q.question}
                          </span>
                        </label>
                      ))}
                      <p className="text-xs text-gray-400 mt-1">{selectedQuestionIds.length} selected</p>
                    </div>
                  )}

                  {bankSelectionMode === 'random' && (
                    <div>
                      <label className="block text-gray-700 text-sm font-medium">
                        Random questions: {bankRandomCount}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max={selectedBank.questions.length}
                        value={Math.min(bankRandomCount, selectedBank.questions.length)}
                        onChange={(e) => setBankRandomCount(parseInt(e.target.value))}
                        className="w-full mt-1"
                      />
                      <p className="text-xs text-gray-400">Out of {selectedBank.questions.length} available</p>
                    </div>
                  )}

                  {/* Extra AI questions */}
                  <div className="border-t border-gray-100 pt-3">
                    <label className="block text-gray-700 text-sm font-medium">
                      Also generate AI questions (optional)
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <input
                        type="text"
                        value={extraAiTopic}
                        onChange={(e) => setExtraAiTopic(e.target.value)}
                        className={inputClasses}
                        placeholder="AI topic"
                      />
                      <input
                        type="number"
                        value={extraAiCount}
                        onChange={(e) => setExtraAiCount(parseInt(e.target.value) || 0)}
                        className={inputClasses}
                        min="0"
                        max="50"
                        placeholder="Count"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Language hint (not for bank tab) */}
          {activeTab !== 'bank' && (
            <div className="flex items-center gap-2 px-3 py-2 bg-primary-50/50 rounded-lg">
              <Globe size={14} className="text-primary flex-shrink-0" />
              <p className="text-xs text-gray-600">
                The exam will be generated in the language you write.{' '}
                {activeTab === 'pdf' && 'PDF content language will be preserved.'}
                {activeTab === 'url' && 'Page content language will be preserved.'}
                {activeTab === 'youtube' && 'Video transcript language will be preserved.'}
              </p>
            </div>
          )}

          {/* Common fields (hide question count for bank tab) */}
          {activeTab !== 'bank' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-700 text-sm font-medium">Questions</label>
                <input
                  type="number"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
                  className={inputClasses}
                  min="1"
                  max="50"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium">Timer (seconds)</label>
                <select
                  value={timerSeconds}
                  onChange={(e) => setTimerSeconds(parseInt(e.target.value))}
                  className={inputClasses}
                >
                  {TIMER_OPTIONS.map(t => (
                    <option key={t} value={t}>{t}s</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-gray-700 text-sm font-medium">Timer (seconds)</label>
              <select
                value={timerSeconds}
                onChange={(e) => setTimerSeconds(parseInt(e.target.value))}
                className={inputClasses}
              >
                {TIMER_OPTIONS.map(t => (
                  <option key={t} value={t}>{t}s</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-xl hover:opacity-90 transition font-medium shadow-sm"
            >
              Create Exam
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateExamModal;
